import React from "react"
import FloatingActionButton from "material-ui/FloatingActionButton"
import ContentAdd from "material-ui/svg-icons/content/add"
import Paper from "material-ui/Paper"
import CircularProgress from "material-ui/CircularProgress"
import autobind from "class-autobind"
import TableActionBar from "../../components/layout/TableActionBar"
import DeviceStore from "../../stores/DeviceStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import strings from "../../localization/strings"
import DeviceTable from "../../components/device/DeviceTable"
import AddDeviceDialog from "../../components/device/AddDeviceDialog"
import AuthenticationStore from "../../stores/AuthenticationStore"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import * as deviceActions from "../../actions/DeviceActions"
import * as reportAction from "../../actions/ReportAction"
import * as permissionHelper from "../../helpers/permissionHelper"
import { IDeviceDocument, EAddDeviceValidationType, IDeviceReportParameters, IDeviceReportColumns } from "../../models"
import * as api from "../../api"
interface IState {
	devices: IDeviceDocument[]
	selectedDevices: IDeviceDocument[]
	page: number
	limit: number
	search: string
	showTableActionBar: boolean
	editableDevice: IDeviceDocument
	loading: boolean
	showAddDialog: boolean
	deviceColumns: IDeviceReportColumns
}

export default class Devices extends React.Component<null, IState> {
	private searchTimeout
	constructor(props) {
		super(props)
		const deviceColumns: IDeviceReportColumns = localStorage.getItem("DeviceColumns") ?
			JSON.parse(localStorage.getItem("DeviceColumns")) :
			{
				IncludeName: true,
				IncludeHomeHierarchy: true,
				IncludeHomeJob: AccountStore.getJobsEnabled(),
				IncludeConnected: true,
				IncludeType: true,
				IncludePhoneNumber: AccountStore.getIVREnabled(),
				IncludeCode: true,
				IncludeOS: true,
				IncludeDescription: true,
				IncludeAppVersion: true,
				IncludeActive: true,
				IncludeDevicePermissions: true
			}
		if (!AccountStore.getJobsEnabled())
			deviceColumns.IncludeHomeJob = false

		this.state = {
			showAddDialog: false,
			loading: true,
			devices: [],
			selectedDevices: [],
			page: 0,
			limit: DeviceStore.getLimit(),
			search: DeviceStore.getSearch(),
			showTableActionBar: true,
			editableDevice: null,
			deviceColumns
		}
		autobind(this)
	}

	componentDidMount() {
		document.title = `${strings.Devices} | ${strings.Findd_Grandd_Central}`
		DeviceStore.on("change", this.devicesChangeEvent)
		HierarchySettingsStore.on("change", this.devicesChangeEvent)
		this.getTopLevelDevices()
	}

	reloadDevicesTable() {
		this.getTopLevelDevices()
	}

	componentWillUnmount() {
		DeviceStore.removeListener("change", this.devicesChangeEvent)
		HierarchySettingsStore.removeListener("change", this.devicesChangeEvent)
		deviceActions.setFilterText()
	}

	devicesChangeEvent() {
		this.setState({
			devices: DeviceStore.getDevices(),
			page: DeviceStore.getPage(),
			search: DeviceStore.getSearch(),
			limit: DeviceStore.getLimit(),
			selectedDevices: [],
			loading: false
		})
	}

	getTopLevelDevices() {
		this.setState({ loading: true })
		const { page, limit, search } = this.state
		const hierarchyItemId = HierarchySettingsStore.getGlobalLevelItemFilterId()
		deviceActions.getTopLevelDevices(hierarchyItemId, page, limit, search)
	}

	getTopLevelDevicesDataDownload() {
		const deviceReportParams = {
			Page: 0,
			Limit: DeviceStore.getTotalDeviceCount(),
			Search: this.state.search,
			LevelIdFilter: HierarchySettingsStore.getGlobalLevelItemFilterId(),
			ReportColumns: this.state.deviceColumns
		} as IDeviceReportParameters
		reportAction.downloadDevicesData(AccountStore.getCurrentAccount().Id, deviceReportParams)
	}

	onSearch(search) {
		if (this.searchTimeout) clearTimeout(this.searchTimeout)
		this.setState({
			search,
			page: 0,
			loading: true,
			devices: [],
			selectedDevices: []
		}, () => {
			this.searchTimeout = setTimeout(() => {
				this.getTopLevelDevices()
			}, 880)
		})
	}

	initPage() {
		this.setState({
			devices: [],
			selectedDevices: [],
			page: 0,
			search: "",
			loading: true
		})
	}

	handleDelete(selectedDevices) {
		AppStateStore.setProgressSnackbarContent("loading")
		const promiseArray = []
		selectedDevices.forEach(device => {
			promiseArray.push(
				new Promise((resolve, reject) => {
					deviceActions.removeDevice(device.Id).then(res => resolve(res))
				})
			)
		})
		Promise.all(promiseArray).then(() => {
			this.getTopLevelDevices()
			AppStateStore.setProgressSnackbarContent(strings.Device_deleted_successfully)
		})
	}

	addDevice(device) {
		return deviceActions.addDevice(device).then(res => {
			const result = res.data as EAddDeviceValidationType
			result === EAddDeviceValidationType.Valid &&
				this.getTopLevelDevices()
			return result
		})
	}

	updateDevice(device) {
		return deviceActions.updateDevice(device).then(() => {
			this.getTopLevelDevices()
		})
	}

	render() {
		const editPermission = permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.EditAllDevices)
		return (
			<div className="page-container Users">
				<div className="page-container-parent">
					{permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.AddAllDevices) &&
						<FloatingActionButton data-intercom-target={"menu-Devices-Btn-Add"} className="fixed-add" style={{ top: 83, right: 40, zIndex: 998 }} secondary={true} onClick={() => this.setState({ showAddDialog: true, editableDevice: null })}>
							<ContentAdd />
						</FloatingActionButton>
					}
					<Paper zDepth={1} className="page-paper">
						{this.state.showTableActionBar &&
							<TableActionBar
								rowSelectionCount={this.state.selectedDevices.length}
								singleSelectedMessage={strings.Device_Selected}
								multipleSelectedMessage={strings.x_Devices_Selected}
								title={strings.Devices}
								onSearch={this.onSearch}
								reloadTable={this.reloadDevicesTable}
								exportTable={this.getTopLevelDevicesDataDownload}
								searchText={this.state.search}
								onDeleteClick={() =>
									AppStateStore.setConfirmDialogContent({
										title: strings.Delete_Device,
										content: this.state.selectedDevices.length > 1 ? strings.Are_you_sure_delete_devices : strings.Are_you_sure_delete_device,
										confirm: () => this.handleDelete(this.state.selectedDevices)
									})}
								readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.DeleteAllDevices)}
								onCloseSearchClick={() => this.setState({ devices: [], selectedDevices: [], page: 0, search: "", loading: true })}
								onSyncClick={permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.InitDevices) ?
									() => {
										AppStateStore.setConfirmDialogContent({
											content: `<p>${strings.Are_you_sure_you_want_to_reinitialize_the_selected_devices}</p><p>${strings.Warning_This_will_act_as_a_fresh_install_and_mobile_data_will_be_deleted}</p>`,
											confirm: () => {
												api.initDevices(this.state.selectedDevices.map(i => i.Id))
												this.setState({ selectedDevices: [] })
											}
										})
									} : null
								}
							/>
						}
						<div style={{ padding: 20 }}>
							{this.state.loading ?
								<div className="spinner">
									<CircularProgress />
								</div> :
								<div>
									{!this.state.devices.length ?
										<div>
											<h2 style={{ textAlign: "center", marginTop: 25 }}>
												{strings.No_device_found}
											</h2>
										</div> :
										<DeviceTable
											devices={this.state.devices}
											page={this.state.page}
											limit={this.state.limit}
											total={DeviceStore.getTotalDeviceCount()}
											limitOptions={DeviceStore.getLimitOption()}
											handleSelect={selectedDevices => this.setState({ selectedDevices })}
											onPageClick={page => this.setState({ page }, () => this.getTopLevelDevices())}
											onLimitChange={limit => this.setState({ limit, page: 0 }, () => this.getTopLevelDevices())}
											onRowClick={device => this.setState({ showAddDialog: true, editableDevice: device })}
											selectPermission={
												permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.DeleteAllDevices) ||
												permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.InitDevices)
											}
											editPermission={editPermission}
											selectedDevices={this.state.selectedDevices}
											deviceColumns={this.state.deviceColumns}
											tableColumnSelect={[
												{ label: strings.Name, show: true, value: this.state.deviceColumns.IncludeName, propertyName: "IncludeName" },
												{ label: strings.Home_Hierarchy, show: true, value: this.state.deviceColumns.IncludeHomeHierarchy, propertyName: "IncludeHomeHierarchy" },
												{ label: strings.Home_Job, show: AccountStore.getJobsEnabled(), value: this.state.deviceColumns.IncludeHomeJob, propertyName: "IncludeHomeJob" },
												{ label: strings.Connected, show: true, value: this.state.deviceColumns.IncludeConnected, propertyName: "IncludeConnected" },
												{ label: strings.Type, show: true, value: this.state.deviceColumns.IncludeType, propertyName: "IncludeType" },
												{ label: strings.Phone_Number, show: AccountStore.getIVREnabled(), value: this.state.deviceColumns.IncludePhoneNumber, propertyName: "IncludePhoneNumber" },
												{ label: strings.Code, show: true, value: this.state.deviceColumns.IncludeCode,propertyName: "IncludeCode"},
												{ label: strings.Mode, show: true, value: this.state.deviceColumns.IncludeMode, propertyName: "IncludeMode" },
												{ label: strings.os, show: true, value: this.state.deviceColumns.IncludeOS, propertyName: "IncludeOS" },
												{ label: strings.Description, show: true, value: this.state.deviceColumns.IncludeDescription, propertyName: "IncludeDescription" },
												{ label: strings.App_Version, show: true, value: this.state.deviceColumns.IncludeAppVersion, propertyName: "IncludeAppVersion" },
												{ label: strings.Active, show: true, value: this.state.deviceColumns.IncludeActive, propertyName: "IncludeActive" },
												{ label: strings.Device_Permissions, show: true, value: this.state.deviceColumns.IncludeDevicePermissions, propertyName: "IncludeDevicePermissions" }
											]}
											handleChangeTableColumns={columns => {
												const deviceColumns = {} as IDeviceReportColumns
												columns.forEach(i => {
													deviceColumns[i.propertyName] = i.value
												})
												this.setState({ deviceColumns })
												localStorage.setItem("DeviceColumns", JSON.stringify(deviceColumns))
											}}
										/>
									}
								</div>
							}
						</div>
					</Paper>
				</div>
				{this.state.showAddDialog &&
					<AddDeviceDialog
						readonly={!editPermission}
						editableDevice={this.state.editableDevice}
						handleClose={() => this.setState({ showAddDialog: false })}
						handleAdd={this.addDevice}
						handleUpdate={this.updateDevice}
					/>
				}
			</div>
		)
	}
}
